/**
 * Tags: free-text labels a person puts on photos ("pool time", "יוֹם רִאשׁוֹן") and on spreads, so
 * the tray can be narrowed to what belongs on the page being edited, and the page can be filled from
 * exactly those pictures. Tags are per album, never printed, and travel with backups.
 *
 * One normaliser for every writer — the editor, the API and the document commands — so "Pool time",
 * " pool  time " and "#pool time" are the same tag, and comparisons elsewhere go through `tagKey`.
 */
import { z } from 'zod';
import type { AlbumDocument, PhotoInfo } from './schema';

export const TAG_LIMITS = {
  maxLength: 40,
  maxPerPhoto: 20,
  maxPerSpread: 20,
  /** photos one tagging request may touch */
  maxPerRequest: 1000,
} as const;

/** Trim, collapse whitespace, drop a leading `#`, cap the length. `null` when nothing is left. */
export function normalizeTag(raw: string): string | null {
  const tag = raw.replace(/\s+/g, ' ').trim().replace(/^[#＃]+\s*/, '').slice(0, TAG_LIMITS.maxLength).trim();
  return tag.length > 0 ? tag : null;
}

/** Identity for comparisons: Latin case is ignored (Hebrew has no case, so it is untouched). */
export const tagKey = (tag: string): string => tag.toLocaleLowerCase();

/** Normalised, de-duplicated (by `tagKey`, first spelling wins), capped at `max`. Order is kept. */
export function normalizeTags(raw: readonly string[], max: number = TAG_LIMITS.maxPerPhoto): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const tag = normalizeTag(item);
    if (!tag) continue;
    const key = tagKey(tag);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(tag);
    if (out.length >= max) break;
  }
  return out;
}

export function hasTag(tags: readonly string[], tag: string): boolean {
  const key = tagKey(tag);
  return tags.some((t) => tagKey(t) === key);
}

export function sharesTag(a: readonly string[], b: readonly string[]): boolean {
  return a.some((t) => hasTag(b, t));
}

export function sameTags(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((t, i) => t === b[i]);
}

/** Accepted on the wire; normalisation happens in the command / route, not in the schema. */
export const TagList = z.array(z.string().min(1).max(TAG_LIMITS.maxLength)).max(TAG_LIMITS.maxPerPhoto);

export interface TagSummary {
  tag: string;
  /** ready photos carrying the tag */
  photos: number;
  /** indexes of the spreads carrying the tag, in album order */
  spreads: number[];
}

/**
 * Every tag in the album — on photos or on spreads — with where it is used. Tags that only differ in
 * case are merged under the spelling seen first. Ordered by first spread, then by photo count, then
 * by name, so what is on the page comes before what is only in the tray.
 */
export function collectTags(
  doc: Pick<AlbumDocument, 'spreads'>,
  photos: Iterable<Pick<PhotoInfo, 'tags' | 'status'>>,
): TagSummary[] {
  const byKey = new Map<string, TagSummary>();
  const entry = (tag: string): TagSummary => {
    const key = tagKey(tag);
    let item = byKey.get(key);
    if (!item) {
      item = { tag, photos: 0, spreads: [] };
      byKey.set(key, item);
    }
    return item;
  };
  doc.spreads.forEach((spread, index) => {
    for (const tag of spread.tags) entry(tag).spreads.push(index);
  });
  for (const photo of photos) {
    if (photo.status !== 'ready') continue;
    for (const tag of photo.tags) entry(tag).photos += 1;
  }
  return [...byKey.values()].sort((a, b) => {
    const as = a.spreads[0] ?? Number.POSITIVE_INFINITY;
    const bs = b.spreads[0] ?? Number.POSITIVE_INFINITY;
    if (as !== bs) return as - bs;
    if (a.photos !== b.photos) return b.photos - a.photos;
    return a.tag.localeCompare(b.tag);
  });
}
